/**
 * AuditLogPage — role-filtered audit log (AUD-02 / AUD-03).
 *
 * Filter UI:
 *   - Content Admin / Super-admin: action + entity_type + entity_id + user_id + from_ts + to_ts
 *   - HR roles: same filters EXCEPT user_id (the backend would scope it anyway,
 *     but hiding the input avoids surfacing a control that has no effect)
 *
 * Pagination: limit/offset with Prev / Next buttons; "Showing N-M of total".
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatValidationError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { TOKENS } from "@/lib/tokens";
import type { Role } from "@/types/api";

import { useAudit, type AuditFilters } from "./useAudit";

const PRIVILEGED: ReadonlySet<Role> = new Set([
  "content_admin",
  "super_admin",
] as Role[]);

const ACTION_OPTIONS = [
  "auth.login",
  "auth.login_failed",
  "auth.logout",
  "auth.password_rotated",
  "user.create",
  "user.update",
  "user.password_reset",
  "compliance.update",
  "compliance.mode_switch",
  // Phase 2 events (#112) — every action string actually emitted by services
  // (grep-verified 2026-05-29 across backend/app/services + api). The plan
  // claimed 9; the codebase emits 13 (position.patch_step, template.update,
  // template.archive, question.edit were undercounted). Covering all 13 keeps
  // both the filter dropdown AND the Acción column label-complete.
  "position.create",
  "position.activate",
  "position.activate_attempt_rejected",
  "position.patch_step",
  "position.close",
  "position.assign",
  "position.unassign",
  "position.regenerate_link",
  "template.create",
  "template.update",
  "template.archive",
  "question.import",
  "question.edit",
];

const PAGE_SIZE = 25;

function actionLabel(t: (k: string) => string, action: string): string {
  // Map backend action strings to i18n keys (dot → underscore).
  // When a translation is missing, i18next can return EITHER the full key
  // (`audit:actions.position_create`) OR the namespace-stripped key
  // (`actions.position_create`) depending on resolution path. Check BOTH forms
  // before falling back to the raw action string. (Wave C #112 sibling-cleanup:
  // ports the fixed helper shipped in Wave B PositionAuditoriaTab.tsx — the
  // previous version only checked the full-key form and would surface the
  // stripped key as text for any missing translation.)
  const flatKey = `actions.${action.replace(/\./g, "_")}`;
  const namespacedKey = `audit:${flatKey}`;
  const translated = t(namespacedKey);
  return translated === namespacedKey || translated === flatKey
    ? action
    : translated;
}

export function AuditLogPage() {
  const { t } = useTranslation(["audit", "common", "users"]);
  const user = useAuthStore((s) => s.user);
  const canFilterByUser = user ? PRIVILEGED.has(user.role) : false;

  const [filters, setFilters] = useState<AuditFilters>({
    limit: PAGE_SIZE,
    offset: 0,
  });
  const [draft, setDraft] = useState<AuditFilters>(filters);
  const audit = useAudit(filters);

  function applyFilters() {
    setFilters({ ...draft, offset: 0 });
  }
  function clearFilters() {
    const cleared = { limit: PAGE_SIZE, offset: 0 };
    setDraft(cleared);
    setFilters(cleared);
  }
  function setOffset(next: number) {
    setFilters((f) => ({ ...f, offset: next }));
  }

  const offset = filters.offset ?? 0;
  const limit = filters.limit ?? PAGE_SIZE;

  return (
    <section className="max-w-6xl mx-auto">
      <h1
        className="text-2xl font-semibold mb-4"
        style={{ color: TOKENS.text }}
      >
        {t("audit:title")}
      </h1>

      <div
        className="grid gap-3 mb-4 p-4 rounded-md"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          backgroundColor: TOKENS.surfaceElevated,
          border: `1px solid ${TOKENS.border}`,
        }}
      >
        <div>
          <Label htmlFor="filter-action">{t("audit:filters.action")}</Label>
          <select
            id="filter-action"
            className="w-full rounded-md px-3"
            style={{
              minHeight: "44px",
              border: `1px solid ${TOKENS.borderStrong}`,
              backgroundColor: TOKENS.surfaceElevated,
              color: TOKENS.text,
            }}
            value={draft.action ?? ""}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                action: e.target.value || undefined,
              }))
            }
          >
            <option value="">{t("audit:filters.all_actions")}</option>
            {ACTION_OPTIONS.map((a) => (
              <option key={a} value={a}>
                {actionLabel(t, a)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label htmlFor="filter-entity-type">
            {t("audit:filters.entity_type")}
          </Label>
          <Input
            id="filter-entity-type"
            type="text"
            value={draft.entity_type ?? ""}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                entity_type: e.target.value || undefined,
              }))
            }
          />
        </div>

        <div>
          <Label htmlFor="filter-entity-id">
            {t("audit:filters.entity_id")}
          </Label>
          <Input
            id="filter-entity-id"
            type="number"
            min={1}
            value={draft.entity_id ?? ""}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                entity_id: e.target.value ? Number(e.target.value) : undefined,
              }))
            }
          />
        </div>

        {canFilterByUser && (
          <div>
            <Label htmlFor="filter-user-id">
              {t("audit:filters.user_id")}
            </Label>
            <Input
              id="filter-user-id"
              type="number"
              min={1}
              value={draft.user_id ?? ""}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  user_id: e.target.value ? Number(e.target.value) : undefined,
                }))
              }
            />
          </div>
        )}

        <div>
          <Label htmlFor="filter-from">{t("audit:filters.from_ts")}</Label>
          <Input
            id="filter-from"
            type="datetime-local"
            value={draft.from_ts ?? ""}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                from_ts: e.target.value || undefined,
              }))
            }
          />
        </div>

        <div>
          <Label htmlFor="filter-to">{t("audit:filters.to_ts")}</Label>
          <Input
            id="filter-to"
            type="datetime-local"
            value={draft.to_ts ?? ""}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                to_ts: e.target.value || undefined,
              }))
            }
          />
        </div>

        {/* AUD-04 (D-06): include-archived toggle — Content Admin / Super-admin
            only (HR never sees it; backend also ignores the param for HR). */}
        {canFilterByUser && (
          <div
            className="flex flex-row items-center gap-2 self-end"
            style={{ minHeight: "44px" }}
          >
            <Checkbox
              id="filter-include-archived"
              checked={!!draft.include_archived}
              onCheckedChange={(checked) =>
                setDraft((d) => ({
                  ...d,
                  include_archived: checked === true ? true : undefined,
                }))
              }
            />
            <Label
              htmlFor="filter-include-archived"
              style={{ cursor: "pointer" }}
            >
              {t("audit:filters.include_archived")}
            </Label>
          </div>
        )}

        <div className="flex flex-row gap-2 items-end">
          <Button
            type="button"
            onClick={applyFilters}
            style={{
              backgroundColor: TOKENS.primary,
              color: TOKENS.primaryFg,
              minHeight: "44px",
              fontWeight: 600,
            }}
          >
            {t("audit:filters.apply")}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={clearFilters}
            style={{ minHeight: "44px" }}
          >
            {t("audit:filters.clear")}
          </Button>
        </div>
      </div>

      <div
        className="rounded-md overflow-x-auto"
        style={{
          backgroundColor: TOKENS.surfaceElevated,
          border: `1px solid ${TOKENS.border}`,
          boxShadow: TOKENS.shadow,
        }}
      >
        {audit.isPending && (
          <div className="p-6" style={{ color: TOKENS.textMuted }}>
            {t("common:actions.loading")}
          </div>
        )}
        {audit.isError && (
          <div className="p-6" role="alert" style={{ color: "#dc2626" }}>
            {formatValidationError(audit.error)}
          </div>
        )}
        {audit.data && audit.data.items.length === 0 && (
          <div className="p-6" style={{ color: TOKENS.textMuted }}>
            {t("audit:table.no_rows")}
          </div>
        )}
        {audit.data && audit.data.items.length > 0 && (
          <table className="w-full min-w-[640px] text-sm">
            <thead style={{ backgroundColor: TOKENS.surface }}>
              <tr>
                <th
                  className="text-left p-3 sticky left-0 z-10"
                  style={{
                    color: TOKENS.text,
                    backgroundColor: TOKENS.surface,
                    boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
                  }}
                >
                  {t("audit:table.timestamp")}
                </th>
                <th className="text-left p-3" style={{ color: TOKENS.text }}>
                  {t("audit:table.user")}
                </th>
                <th className="text-left p-3" style={{ color: TOKENS.text }}>
                  {t("audit:table.action")}
                </th>
                <th className="text-left p-3" style={{ color: TOKENS.text }}>
                  {t("audit:table.entity")}
                </th>
                <th className="text-left p-3" style={{ color: TOKENS.text }}>
                  {t("audit:table.diff")}
                </th>
              </tr>
            </thead>
            <tbody>
              {audit.data.items.map((row) => (
                <tr
                  key={row.id}
                  style={{ borderTop: `1px solid ${TOKENS.border}` }}
                >
                  <td
                    className="p-3 align-top whitespace-nowrap sticky left-0 z-10"
                    style={{
                      color: TOKENS.text,
                      backgroundColor: TOKENS.surfaceElevated,
                      boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
                    }}
                  >
                    {row.timestamp}
                    {row.is_archived && (
                      <Badge variant="secondary" className="ml-2 align-middle">
                        {t("audit:table.archived_badge")}
                      </Badge>
                    )}
                  </td>
                  <td className="p-3 align-top" style={{ color: TOKENS.text }}>
                    {row.user_id !== null
                      ? `#${row.user_id}${row.user_role ? ` · ${t(`users:roles.${row.user_role}`)}` : ""}`
                      : t("audit:table.system_user")}
                  </td>
                  <td className="p-3 align-top" style={{ color: TOKENS.text }}>
                    {actionLabel(t, row.action)}
                  </td>
                  <td className="p-3 align-top" style={{ color: TOKENS.text }}>
                    {row.entity_type}#{row.entity_id}
                  </td>
                  <td className="p-3 align-top" style={{ color: TOKENS.text }}>
                    {row.diff ? (
                      <details>
                        <summary
                          style={{
                            cursor: "pointer",
                            color: TOKENS.textMuted,
                          }}
                        >
                          {t("audit:table.diff")}
                        </summary>
                        <pre
                          className="mt-1 p-2 rounded text-xs"
                          style={{
                            backgroundColor: TOKENS.surface,
                            border: `1px solid ${TOKENS.border}`,
                            color: TOKENS.text,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {JSON.stringify(row.diff, null, 2)}
                        </pre>
                      </details>
                    ) : (
                      <span style={{ color: TOKENS.textMuted }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {audit.data && audit.data.total > 0 && (
        <div className="flex flex-row items-center justify-between mt-3">
          <span style={{ color: TOKENS.textMuted, fontSize: "0.875rem" }}>
            {t("audit:pagination.showing", {
              from: offset + 1,
              to: Math.min(offset + limit, audit.data.total),
              total: audit.data.total,
            })}
          </span>
          <div className="flex flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              style={{ minHeight: "44px" }}
            >
              {t("audit:pagination.prev")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= audit.data.total}
              style={{ minHeight: "44px" }}
            >
              {t("audit:pagination.next")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
